import './styles.css'
import '@mantine/core/styles.css'

import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { LoadingOverlay, MantineProvider } from '@mantine/core'
import { QueryErrorResetBoundary, useIsRestoring } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import { theme } from './theme'
import reportWebVitals from './reportWebVitals'
import { queryClient } from './queries/config'
import { authService } from './services'
import { env } from './env'
import { initializeAnalytics } from './integrations/analytics'
import NotFound from './components/ui/NotFound'
import InternalError from './components/ui/InternalError'
import * as TanStackQueryProvider from '@/integrations/tanstack-query/root-provider'

export const router = createRouter({
  routeTree,
  context: {
    // @ts-expect-error
    authContext: {},
    queryClient,
    isRestoring: false,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  notFoundMode: 'root',
  defaultPendingComponent: () => (
    <LoadingOverlay visible={true} zIndex={1000} overlayProps={{ blur: 2 }} />
  ),
  defaultErrorComponent: InternalError,
  defaultNotFoundComponent: NotFound,
})

const trackPageView = initializeAnalytics({
  measurementId: env.VITE_GA_MEASUREMENT_ID,
  environment: env.VITE_APP_ENV,
  appUrl: env.VITE_APP_URL,
})

if (trackPageView) {
  router.subscribe('onResolved', ({ toLocation }) => {
    trackPageView(toLocation.href)
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }

  interface RouteContext {
    authContext: typeof authService
    queryClient: typeof queryClient
    isRestoring: boolean
  }
}

Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  enabled: !!env.VITE_SENTRY_DSN,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  tracesSampleRate: env.VITE_APP_ENV === 'production' ? 0.1 : 1.0,
  replaysSessionSampleRate: env.VITE_APP_ENV === 'production' ? 0.05 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: true,
  environment: env.VITE_APP_ENV,
  release: env.VITE_APP_VERSION,
  debug: import.meta.env.DEV,
})

function App() {
  const isRestoring = useIsRestoring()

  return (
    <MantineProvider theme={{ ...theme, fontFamily: 'Lexend, sans-serif' }}>
      <TanStackQueryProvider.Provider>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <Sentry.ErrorBoundary onReset={reset}>
              <RouterProvider
                router={router}
                context={{ authContext: authService, queryClient, isRestoring }}
              />
            </Sentry.ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </TanStackQueryProvider.Provider>
    </MantineProvider>
  )
}

const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
